import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { PriceListListItem, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-price-lists',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.priceLists.title')" [description]="t('sales.priceLists.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.priceLists.fields.code') }}</th>
                <th>{{ t('sales.priceLists.fields.name') }}</th>
                <th>{{ t('sales.priceLists.fields.currency') }}</th>
                <th>{{ t('sales.priceLists.fields.validFrom') }}</th>
                <th>{{ t('sales.priceLists.fields.validTo') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ row.validFromUtc | ainDate }}</td>
                <td>{{ row.validToUtc ? (row.validToUtc | ainDate) : '-' }}</td>
                <td>{{ row.isActive ? t('sales.priceLists.active') : t('sales.priceLists.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
  `]
})
export class SalesPriceListsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<PriceListListItem[]>([]);
  readonly selectedRows = signal<PriceListListItem[]>([]);

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listPriceLists().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.priceLists.errorLoad')); this.loading.set(false); }
    });
  }

  onSelectionChange(rows: PriceListListItem[]): void { this.selectedRows.set(rows ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/price-lists/new']); }
  openSelected(): void { const row = this.selectedRows()[0]; if (row) this.openDetail(row.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/price-lists', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
